import React from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import SoftBadge from 'components/SoftBadge';
import CheckCircle from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';
import SoftButton from 'components/SoftButton';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

function CustomTooltip({ toggleHelp }) {
  return (     
      <SoftBox display="flex" flexDirection="column" justifyContent="flex-start" bgColor="light" p={3} borderRadius="8px" >
          <SoftBox display="flex" alignItems="center" justifyContent="space-between" >
            <SoftTypography mb={2} color="dark" variant="h6" gutterBottom sx={{fontWeight: 'medium', textAlign: 'left'}}>
            Tips on how to craft a good and effective prompt
            </SoftTypography>
            <SoftButton variant="outlined" size="medium" color="dark" onClick={toggleHelp}>
                Close help
            </SoftButton>
          </SoftBox>
          <SoftBadge
            variant="contained"
            color="warning"
            size="md"
            sx = {{ mb: 1, mt:3, ml: -0.5 }}
            badgeContent={
                <SoftTypography variant="button">Important to Know</SoftTypography>
            }
          />
          <SoftTypography variant="button" ml={1} sx={{textAlign: 'left'}}>
            It&apos;s crucial to understand that our system is already optimized to adhere to best practices in general and specifically for healthcare providers. Key aspects to remember include:
          </SoftTypography>
          <SoftTypography variant="button" color="text" ml={1} mt={1} sx={{textAlign: 'left'}}>
              - <b>Automatic inclusion of the transcript</b> alongside your prompt, providing comprehensive context for the AI.
          </SoftTypography>
          <SoftTypography variant="button" color="text" ml={1} mt={0.5} mb={3} sx={{textAlign: 'left'}}>
              - <b>Pre-configured system settings</b> that recognize healthcare professional needs, ensuring relevant and precise responses.
          </SoftTypography>
          <SoftBadge
            variant="contained"
            color="warning"
            size="md"
            sx = {{ mb: 1, ml: -0.5 }}
            badgeContent={
                <SoftTypography variant="button">How You Can Enhance the AI&apos;s Output</SoftTypography>
            }
          />
          <SoftTypography color="dark" variant="button" mt={0.5} ml={1} sx={{textAlign: 'left'}}>
              While our system ensures the foundational settings for healthcare contexts, your input remains invaluable in directing the AI to meet your specific needs. Here are tips on how to refine the output:
          </SoftTypography>
          <SoftBox mb={4} mt={1.5} ml={0.5} display="flex" alignItems="center">
            <InfoOutlined /> 
            <SoftTypography variant="button" color="text" ml={0.7}>
            You can also consult our AI Assistant buddy, conveniently located in the bottom right corner, for prompt assistance.
            </SoftTypography>
          </SoftBox>
          <SoftTypography mb={1} ml={0.5}  alignItems="left" color="text" variant="button" gutterBottom sx={{fontWeight: 'regular', textAlign: 'left'}}>
          Use the Who, What and How principle.
          </SoftTypography>
          <List dense sx={{ borderLeft: '4px solid #344767', paddingLeft: '16px', paddingRight: '30px', paddingTop: '10px', paddingBottom: '10px', backgroundColor: '#FFFFFF', borderRadius: '4px',}}>
              <ListItem>
                  <ListItemText
                      primary={
                      <>
                          <b>Define the role</b> of the ai model, for example: &apos;Be a Therapist&apos;  or  &apos;Take the role of a Therapist&apos;.
                      </>
                      }
                      primaryTypographyProps={{
                      variant: "body2",
                      mb: 1, 
                      }}
                  />
                  </ListItem>
              <ListItem>
                  <ListItemText 
                      primary={
                      <>
                          Clearly <b>outline the task</b> and or the questions you want the AI to answer or perform, for example: &apos;I want you to...&apos;  or  &apos;Your task is to...&apos;.
                      </>
                      }
                      primaryTypographyProps={{ variant: "body2", mb: 1 }}
                  />
              </ListItem>
              <ListItem>
                  <ListItemText 
                      primary={
                      <>
                          <b>Specify how</b> you&apos;d like the AI to approach the task, including any constraints or formats to follow.
                      </>
                      }
                      primaryTypographyProps={{ variant: "body2", mb: 1 }}
                  />
              </ListItem>
          </List>
          <SoftTypography mb={1} ml={0.5} mt={3}  alignItems="left" color="text" variant="button" gutterBottom sx={{fontWeight: 'regular', textAlign: 'left'}}>
              Improve the Detail and Specificity of Your Prompts
          </SoftTypography>
          <List dense sx={{ borderLeft: '4px solid #344767', paddingLeft: '16px', paddingRight: '30px', paddingTop: '10px', paddingBottom: '10px', backgroundColor: '#FFFFFF', borderRadius: '4px',}}>
              <ListItem>
                  <ListItemText
                      primary={
                      <>
                          <b>Replace</b> a general request like:
                      </>
                      }
                      primaryTypographyProps={{
                      variant: "body2",
                      mb: 0.5, 
                      }}
                  />
              </ListItem>
              <ListItem>
                  <ListItemText
                      primary={
                      <>
                          Summarize the conversation with the patient and focus on all the medications spoken about.
                      </>
                      }
                      primaryTypographyProps={{
                      variant: "body2",
                      mb: 2.5, 
                      }}
                  />
              </ListItem>
              <ListItem>
                  <ListItemText
                      primary={
                      <>
                          <b>With:</b> 
                      </>
                      }
                      primaryTypographyProps={{
                      variant: "body2",
                      mb: 0.5, 
                      }}
                  />
              </ListItem>
              <ListItem>
                  <ListItemText
                      primary={
                      <>
                          Synthesize the details of my consultation with my patient regarding their medication regimen.
                      </>
                      }
                      primaryTypographyProps={{
                      variant: "body2",
                      mb: 0.5, 
                      }}
                  />
              </ListItem>
              <ListItem>
                  <ListItemText
                      primary={
                      <>
                          Structure the summary to include the following sections:
                      </>
                      }
                      primaryTypographyProps={{
                      variant: "body2",
                      mb: 2, 
                      }}
                  />
              </ListItem>
              <ListItem>
                  <ListItemText
                      primary={
                      <>
                          Medications Taken in the Past:
                      </>
                      }
                      primaryTypographyProps={{
                      variant: "body2",
                      mb: 0.5, 
                      }}
                  />
              </ListItem>
              <ListItem>
                  <ListItemText
                      primary={
                      <>
                          [ List any medications the patient has taken in the past for their condition, including dosages and duration of treatment. ]
                      </>
                      }
                      primaryTypographyProps={{
                      variant: "body2",
                      mb: 2, 
                      }}
                  />
              </ListItem>
              <ListItem>
                  <ListItemText
                      primary={
                      <>
                          Current Medications:
                      </>
                      }
                      primaryTypographyProps={{
                      variant: "body2",
                      mb: 0.5, 
                      }}
                  />
              </ListItem>
              <ListItem>
                  <ListItemText
                      primary={
                      <>
                          [ Detail all medications the patient is currently taking, focusing on dosage, frequency, and the specific health issue each medication addresses. ]
                      </>
                      }
                      primaryTypographyProps={{
                      variant: "body2",
                      mb: 2, 
                      }}
                  />
              </ListItem>
              <ListItem>
                  <ListItemText
                      primary={
                      <>
                          Prescribed Medications to Take:
                      </>
                      }
                      primaryTypographyProps={{
                      variant: "body2",
                      mb: 0.5, 
                      }}
                  />
              </ListItem>
              <ListItem>
                  <ListItemText
                      primary={
                      <>
                          [ Enumerate any new medications prescribed during the session, along with instructions for use, expected effects, and any necessary lifestyle adjustments or monitoring. ]
                      </>
                      }
                      primaryTypographyProps={{
                      variant: "body2",
                      mb: 0.5, 
                      }}
                  />
              </ListItem>
          </List>
          <SoftTypography ml={1} mt={4} mb={2} color="dark" variant="body2" gutterBottom sx={{fontWeight: 'medium', textAlign: 'left'}}>
              Some &quot;cheatsheet&quot; tips: 
          </SoftTypography>
          <SoftBox ml={1} display="flex" alignItems="center" mb={1}>
              <CheckCircleOutlineOutlinedIcon  />
              <SoftTypography ml={1} color="text" variant="body2">
              Try using &quot;extract&quot; instead of &quot;summarize&quot; when you&apos;re looking for specific information.
              </SoftTypography>
          </SoftBox>
          <SoftBox ml={1} display="flex" alignItems="center" mb={1}>
              <CheckCircleOutlineOutlinedIcon  />
              <SoftTypography ml={1} color="text" variant="body2">
              Directly tell the AI what to do, instead of asking, for clearer responses.
              </SoftTypography>
          </SoftBox>
          <SoftBox ml={1} display="flex" alignItems="center" mb={1}>
              <CheckCircleOutlineOutlinedIcon  />
              <SoftTypography ml={1} color="text" variant="body2">
                In technical fields like healthcare, including specific keywords or terminology in your prompt can improve the relevancy of the AI&apos;s response.
              </SoftTypography>
          </SoftBox>
          <SoftBox ml={1} display="flex" alignItems="center" mb={1}>
              <CheckCircleOutlineOutlinedIcon  />
              <SoftTypography ml={1} color="text" variant="body2">
                  Utilize clear structure: Organize your prompt in a logical order to improve the output and structure of the output.
              </SoftTypography>
          </SoftBox>
          <SoftBox ml={1} display="flex" alignItems="center" mb={1}>
              <CheckCircleOutlineOutlinedIcon  />
              <SoftTypography ml={1} color="text" variant="body2">
                For concise summaries, specify the length directly, such as &quot;summarize in 30 words&quot; or &quot;in 3 sentences,&quot; instead of using general terms like &quot;short&quot; or &quot;concise.&quot;
              </SoftTypography>
          </SoftBox>
      </SoftBox>
        
     
      
  );
}

CustomTooltip.propTypes = {
  toggleHelp: PropTypes.func,
};

export default CustomTooltip;

